/**
 * dedupeFetch.js
 * Lives in src/utils/ next to apiTarget.js and localProjects.js.
 *
 * Problem: the heavy analysis endpoints (data-summary-v2, analyze-collectors-v2,
 * correlation-matrix-v2, ...) get fired 2-3x simultaneously (StrictMode double
 * effects + parent re-renders). Each duplicate costs the server ~30-60s of
 * pandas work, which is what pushes gunicorn workers into WORKER TIMEOUT and
 * produces the 504-dressed-as-CORS errors.
 *
 * Fix: wrap window.fetch once. Identical concurrent POSTs (same URL + same
 * body) to the heavy paths share ONE network request; every caller gets its
 * own clone of the response. Entries clear as soon as the request settles, so
 * a deliberate Refresh click later is a fresh request, never a stale reuse.
 *
 * Import ONCE in index.js, before anything renders:
 *   import './utils/dedupeFetch';
 *
 * Plays fine with the localProjects.js engine interceptor: whichever patches
 * fetch later wraps the other, and both pass through untouched requests.
 */

const HEAVY_PATHS = [
  '/data-summary',        // covers /data-summary/, -v2/, -quick/
  '/analyze-collectors',
  '/analyze-timeline',
  '/correlation-matrix',
  '/run-quality-checks',
  '/run-backcheck-analysis',
];

const inflight = new Map(); // key -> Promise<Response>

(function installDedupeFetch() {
  if (typeof window === 'undefined' || window.__DQ_DEDUPE_FETCH__) return;
  window.__DQ_DEDUPE_FETCH__ = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();

      const isHeavy = method === 'POST' && HEAVY_PATHS.some((p) => url.includes(p));
      // Requests carrying an AbortController signal are excluded: deduping
      // them would let one component's unmount abort everyone else's request.
      const hasSignal = Boolean(init && init.signal);

      if (isHeavy && !hasSignal && typeof (init && init.body) === 'string') {
        const key = url + '|' + init.body;

        if (inflight.has(key)) {
          // Duplicate: share the in-flight request, hand back a clone
          return inflight.get(key).then((res) => res.clone());
        }

        const p = originalFetch(input, init).finally(() => {
          inflight.delete(key);
        });
        inflight.set(key, p);
        return p.then((res) => res.clone());
      }
    } catch (e) {
      /* never break a request over deduping */
    }
    return originalFetch(input, init);
  };
})();